import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzSelectModule, NzSelectOptionInterface } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { saveAs } from 'file-saver';
import { IImagingDetailed } from 'src/app/interfaces/Imaging/IImagingDetailed';
import { PermissionModule } from 'src/app/modules/permission.module';
import { ImagingService } from 'src/app/services/imaging.service';
import { PermissionService } from 'src/app/services/permission.service';
import { SwalToastService } from 'src/app/services/swal.service';
import {
  IDicomImage,
  isDicomBlob,
  parseDicom,
} from 'src/app/utils/dicom-frame-renderer';

type CornerstoneCoreModule = typeof import('@cornerstonejs/core');
type CornerstoneToolsModule = typeof import('@cornerstonejs/tools');
type DicomParserModule = typeof import('dicom-parser');

interface ICornerstoneRuntime {
  core: CornerstoneCoreModule;
  tools: CornerstoneToolsModule;
  dicomParser: DicomParserModule | { default?: DicomParserModule };
}

type ProtectTool = 'window' | 'pan' | 'zoom' | 'rotate' | 'box';

interface IProtectBoundingBox {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

interface IProtectSegmentationResult {
  maskImage?: string;
  overlayImage?: string;
}

interface IProtectDicomFrame {
  imageId: string;
  pathId: number;
  path: string;
  fileName: string;
  frameIndex: number;
  rows: number;
  columns: number;
  instanceNumber: number;
  box?: IProtectBoundingBox;
  annotationUID?: string;
  result?: IProtectSegmentationResult;
  isLoading: boolean;
  segmentError: boolean;
}

interface IProtectDicomMetadata {
  frames: number;
  rows: number;
  columns: number;
  instanceNumber: number;
  hasPixelData: boolean;
  modality?: string;
  sopClassUID?: string;
}

interface IProtectLocalFrame {
  imageId: string;
  pixelData: Uint8Array | Uint16Array | Int16Array;
  rows: number;
  columns: number;
  samplesPerPixel: number;
  bitsAllocated: number;
  bitsStored: number;
  pixelRepresentation: number;
  photometricInterpretation: string;
  windowCenter: number;
  windowWidth: number;
  rescaleIntercept: number;
  rescaleSlope: number;
  minPixelValue: number;
  maxPixelValue: number;
  modality?: string;
}

@Component({
  selector: 'app-protect-med-sam-segmentation',
  standalone: true,
  imports: [
    NzLayoutModule,
    NzModalModule,
    TranslateModule,
    CommonModule,
    NzTooltipModule,
    NzIconModule,
    FormsModule,
    NzButtonModule,
    NzPaginationModule,
    NzSpinModule,
    NzImageModule,
    NzSelectModule,
    PermissionModule,
  ],
  templateUrl: './protect-med-sam-segmentation-component.component.html',
  styleUrl: './protect-med-sam-segmentation-component.component.css',
})
export class ProtectMedSamSegmentationComponentComponent
  implements AfterViewInit, OnDestroy
{
  @Input() imagingRecords: NzSelectOptionInterface[] = [];
  @ViewChild('dicomViewport') dicomViewport?: ElementRef<HTMLDivElement>;

  private static cornerstoneInit?: Promise<ICornerstoneRuntime>;
  private static toolsRegistered = false;
  private static protectImageLoaderRegistered = false;
  private static protectImageFrames = new Map<string, IProtectLocalFrame>();

  private readonly renderingEngineId = `protect-rendering-${crypto.randomUUID()}`;
  private readonly viewportId = `protect-stack-${crypto.randomUUID()}`;
  private readonly toolGroupId = `protect-tools-${crypto.randomUUID()}`;
  private readonly destroy$ = new Subject<void>();

  private cornerstone?: ICornerstoneRuntime;
  private imagingDataRaw: IImagingDetailed[] = [];
  private renderingEngine?: any;
  private toolGroup?: any;
  private viewportReady = false;
  private stackNewImageListener?: EventListener;
  private annotationListener?: EventListener;

  selectedImaging: number | null = null;
  dicomFrames: IProtectDicomFrame[] = [];
  activeFrameIndex = 0;
  activeTool: ProtectTool = 'window';
  isImagingLoading = false;
  isExamLoading = false;
  isSubmitting = false;
  isReconstructing = false;
  isHdrbtSimulationModalVisible = false;
  loadingMessage = 'Loading DICOM stack';
  loadError?: string;
  loadDiagnostics: string[] = [];

  constructor(
    private readonly imagingService: ImagingService,
    private readonly permissionService: PermissionService,
    private readonly swalToastService: SwalToastService,
    private readonly zone: NgZone,
  ) {}

  ngOnInit() {
    this.imagingService.imagingLoadingSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((v) => (this.isImagingLoading = v));

    this.imagingService.imagingDataSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: IImagingDetailed[]) => {
        this.imagingDataRaw = data;

        this.imagingRecords =
          data.map((i) => ({
            label: `${i.examTitle} ${i.date}`,
            value: i.id,
          })) || [];
      });
  }

  ngAfterViewInit() {
    this.viewportReady = true;
  }

  ngOnDestroy() {
    this.destroyViewer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  async onImagingSelected(imagingId: number | null) {
    this.resetState();

    if (!imagingId) return;

    const imaging = this.imagingDataRaw.find((i) => i.id === imagingId);
    const imagePaths = imaging?.paths ?? [];

    if (!imagePaths.length) {
      this.swalToastService.toastError('No DICOM file found');
      return;
    }

    this.isExamLoading = true;
    this.loadingMessage = 'Loading DICOM tools';

    try {
      await this.ensureCornerstoneInitialized();
      this.loadingMessage = 'Reading DICOM files';
      const frames = await this.loadDicomFrames(imagePaths);

      if (!frames.length) {
        this.loadError =
          'Selected imaging record does not contain segmentable DICOM image frames.';
        this.isExamLoading = false;
        return;
      }

      this.dicomFrames = frames;
      await this.waitForAngularRender();
      this.loadingMessage = 'Preparing DICOM viewer';
      await this.initializeDicomViewer();
      this.isExamLoading = false;
    } catch (error) {
      console.error('Protect MedSAM DICOM viewer failed to load', error);
      this.isExamLoading = false;
      this.loadError = 'Unable to load DICOM viewer';
      this.swalToastService.toastError('Unable to load DICOM viewer');
    }
  }

  selectTool(tool: ProtectTool) {
    this.activeTool = tool;
    this.applyToolBindings();
  }

  async goToFrame(index: number) {
    await this.setFrameIndex(index);
  }

  async frameSliderChange(event: Event) {
    const input = event.target as HTMLInputElement;
    await this.setFrameIndex(input.valueAsNumber - 1);
  }

  clearCurrentBox() {
    const frame = this.activeFrame;

    if (!frame?.annotationUID) {
      return;
    }

    this.cornerstone?.tools.annotation.state.removeAnnotation(
      frame.annotationUID,
    );
    frame.annotationUID = undefined;
    frame.box = undefined;
    frame.result = undefined;
    this.getViewport()?.render();
  }

  clearAllBoxes() {
    const element = this.dicomViewport?.nativeElement;
    const tools = this.cornerstone?.tools;

    for (const frame of this.dicomFrames) {
      if (frame.annotationUID) {
        this.cornerstone?.tools.annotation.state.removeAnnotation(
          frame.annotationUID,
        );
      }
    }

    if (element && tools) {
      const remainingAnnotations =
        tools.annotation.state.getAnnotations(
          tools.RectangleROITool.toolName,
          element,
        ) || [];

      remainingAnnotations.forEach((annotation: any) => {
        if (annotation.annotationUID) {
          tools.annotation.state.removeAnnotation(annotation.annotationUID);
        }
      });
    }

    this.dicomFrames.forEach((frame) => {
      frame.annotationUID = undefined;
      frame.box = undefined;
      frame.result = undefined;
      frame.segmentError = false;
    });

    this.syncBoxesFromAnnotations();
    this.getViewport()?.render();
  }

  async segment() {
    if (!this.canSegment) return;

    const viewport = this.getViewport();

    if (!viewport) {
      this.swalToastService.toastError('DICOM viewer is not ready');
      return;
    }

    this.isSubmitting = true;
    const originalFrameIndex = this.activeFrameIndex;

    this.dicomFrames.forEach((frame) => {
      frame.isLoading = true;
      frame.segmentError = false;
      frame.result = undefined;
    });

    for (let index = 0; index < this.dicomFrames.length; index++) {
      const frame = this.dicomFrames[index];

      try {
        await this.setFrameIndex(index, false);
        await this.waitForRender();

        const frameFile = await this.exportNativeFramePng(frame);

        if (!frame.box) {
          frame.segmentError = true;
          frame.isLoading = false;
          continue;
        }

        const res = await firstValueFrom(
          this.imagingService.segmentProtectMedSamDicomFrame(
            frame.pathId,
            frame.frameIndex,
            frameFile,
            frame.box.x1,
            frame.box.y1,
            frame.box.x2,
            frame.box.y2,
          ),
        );

        frame.result = res.data?.results?.[0];
        frame.isLoading = false;
      } catch (error) {
        frame.segmentError = true;
        frame.isLoading = false;
      }
    }

    await this.setFrameIndex(originalFrameIndex, false);
    this.isSubmitting = false;

    if (this.dicomFrames.some((frame) => frame.segmentError)) {
      this.swalToastService.toastError('Some frames could not be segmented');
    }
  }

  reconstructDicoms() {
    if (!this.canReconstructDicoms || !this.selectedImaging) {
      return;
    }

    const frames = this.dicomFrames
      .filter((frame) => frame.result?.maskImage || frame.result?.overlayImage)
      .map((frame) => ({
        imageId: frame.pathId,
        frameIndex: frame.frameIndex,
        maskImage: frame.result?.maskImage,
        overlayImage: frame.result?.overlayImage,
      }));

    this.isReconstructing = true;

    this.imagingService
      .reconstructProtectMedSamDicoms(this.selectedImaging, frames)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          this.isReconstructing = false;
          saveAs(blob, 'protect-medsam-dicoms.zip');
        },
        error: () => {
          this.isReconstructing = false;
          this.swalToastService.toastError('Unable to reconstruct DICOM files');
        },
      });
  }

  openHdrbtSimulationModal() {
    this.isHdrbtSimulationModalVisible = true;
  }

  closeHdrbtSimulationModal() {
    this.isHdrbtSimulationModalVisible = false;
  }

  resetViewer() {
    const viewport = this.getViewport();

    if (!viewport) {
      return;
    }

    viewport.resetCamera();
    viewport.resetProperties();
    viewport.render();
  }

  toggleInvert() {
    const viewport = this.getViewport();

    if (!viewport) {
      return;
    }

    const properties = viewport.getProperties();
    viewport.setProperties({ invert: !properties.invert });
    viewport.render();
  }

  hasPermission(permission: string[]): boolean {
    return this.permissionService.can(permission);
  }

  get activeFrame(): IProtectDicomFrame | undefined {
    return this.dicomFrames.at(this.activeFrameIndex);
  }

  get readyFrameCount(): number {
    return this.dicomFrames.filter((frame) => !!frame.box).length;
  }

  get segmentedFrameCount(): number {
    return this.dicomFrames.filter((frame) => !!frame.result).length;
  }

  get canSegment(): boolean {
    return (
      this.dicomFrames.length > 0 &&
      !this.isExamLoading &&
      !this.isSubmitting &&
      !this.isReconstructing &&
      this.dicomFrames.every((frame) => !!frame.box)
    );
  }

  get canReconstructDicoms(): boolean {
    return (
      !!this.selectedImaging &&
      !this.isSubmitting &&
      !this.isReconstructing &&
      this.dicomFrames.some(
        (frame) => frame.result?.maskImage || frame.result?.overlayImage,
      )
    );
  }

  get activeMaskSrc(): string | undefined {
    const maskImage = this.activeFrame?.result?.maskImage;
    return maskImage ? `data:image/png;base64,${maskImage}` : undefined;
  }

  get activeOverlaySrc(): string | undefined {
    const overlayImage = this.activeFrame?.result?.overlayImage;
    return overlayImage ? `data:image/png;base64,${overlayImage}` : undefined;
  }

  private async initializeDicomViewer() {
    if (!this.viewportReady || !this.dicomViewport?.nativeElement) {
      throw new Error('DICOM viewport is not ready.');
    }

    await this.ensureCornerstoneInitialized();
    this.destroyViewer(false);

    const element = this.dicomViewport.nativeElement;
    element.oncontextmenu = (event) => event.preventDefault();

    const { core } = this.cornerstone!;

    this.renderingEngine = new core.RenderingEngine(this.renderingEngineId);
    this.renderingEngine.enableElement({
      viewportId: this.viewportId,
      element,
      type: core.Enums.ViewportType.STACK,
    });

    const viewport = this.getViewport();
    await this.withTimeout(
      viewport?.setStack(
        this.dicomFrames.map((frame) => frame.imageId),
        0,
      ),
      15000,
      'DICOM stack render timed out',
    );
    viewport?.render();

    this.createToolGroup();
    this.wireViewerEvents();
    this.selectTool('window');

    setTimeout(() => {
      this.renderingEngine?.resize(true, false);
      this.getViewport()?.render();
    });
  }

  private async ensureCornerstoneInitialized() {
    if (!ProtectMedSamSegmentationComponentComponent.cornerstoneInit) {
      ProtectMedSamSegmentationComponentComponent.cornerstoneInit = Promise.all(
        [
          import('@cornerstonejs/core'),
          import('@cornerstonejs/tools'),
          import('dicom-parser'),
        ],
      ).then(async ([core, tools, dicomParser]) => {
        await Promise.all([core.init(), tools.init()]);

        return {
          core,
          tools,
          dicomParser,
        };
      });
    }

    this.cornerstone =
      await ProtectMedSamSegmentationComponentComponent.cornerstoneInit;
    this.registerCornerstoneTools();
    this.registerProtectImageLoader();
  }

  private registerCornerstoneTools() {
    const tools = this.cornerstone?.tools;

    if (!tools) {
      return;
    }

    if (ProtectMedSamSegmentationComponentComponent.toolsRegistered) {
      return;
    }

    [
      tools.WindowLevelTool,
      tools.PanTool,
      tools.ZoomTool,
      tools.PlanarRotateTool,
      tools.StackScrollTool,
      tools.RectangleROITool,
    ].forEach((tool) => {
      try {
        tools.addTool(tool);
      } catch (error) {
        // Tool registration is global; another component instance may have added it.
      }
    });

    ProtectMedSamSegmentationComponentComponent.toolsRegistered = true;
  }

  private registerProtectImageLoader() {
    const core = this.cornerstone?.core;

    if (
      !core ||
      ProtectMedSamSegmentationComponentComponent.protectImageLoaderRegistered
    ) {
      return;
    }

    core.registerImageLoader('protectdicom', (imageId: string) => {
      const frame =
        ProtectMedSamSegmentationComponentComponent.protectImageFrames.get(
          imageId,
        );

      if (!frame) {
        return {
          promise: Promise.reject(
            new Error(`Protect DICOM frame was not found: ${imageId}`),
          ),
        };
      }

      const voxelManager = core.utilities.VoxelManager.createImageVoxelManager({
        id: imageId,
        scalarData: frame.pixelData,
        width: frame.columns,
        height: frame.rows,
        numberOfComponents: frame.samplesPerPixel,
      });

      return {
        promise: Promise.resolve({
          imageId,
          dataType: frame.pixelData.constructor.name,
          color: frame.samplesPerPixel > 1,
          columnPixelSpacing: 1,
          rowPixelSpacing: 1,
          columns: frame.columns,
          height: frame.rows,
          intercept: frame.rescaleIntercept,
          slope: frame.rescaleSlope,
          invert:
            frame.photometricInterpretation.toUpperCase() === 'MONOCHROME1',
          minPixelValue: frame.minPixelValue,
          maxPixelValue: frame.maxPixelValue,
          rows: frame.rows,
          sizeInBytes: frame.pixelData.byteLength,
          width: frame.columns,
          windowCenter: frame.windowCenter,
          windowWidth: frame.windowWidth,
          getPixelData: () => voxelManager.getScalarData(),
          getCanvas: undefined,
          numberOfComponents: frame.samplesPerPixel,
          rgba: false,
          voxelManager,
        } as any),
      };
    });

    core.metaData.addProvider((type: string, imageId: string) => {
      const frame =
        ProtectMedSamSegmentationComponentComponent.protectImageFrames.get(
          imageId,
        );

      if (!frame) {
        return undefined;
      }

      switch (type) {
        case 'imagePixelModule':
          return {
            bitsAllocated: frame.bitsAllocated,
            bitsStored: frame.bitsStored,
            highBit: Math.max(0, frame.bitsStored - 1),
            samplesPerPixel: frame.samplesPerPixel,
            photometricInterpretation: frame.photometricInterpretation,
            pixelRepresentation: frame.pixelRepresentation,
            rows: frame.rows,
            columns: frame.columns,
          };
        case 'imagePlaneModule':
          return {
            rows: frame.rows,
            columns: frame.columns,
            rowPixelSpacing: 1,
            columnPixelSpacing: 1,
            rowCosines: [1, 0, 0],
            columnCosines: [0, 1, 0],
            imagePositionPatient: [0, 0, 0],
            imageOrientationPatient: new Float32Array([1, 0, 0, 0, 1, 0]),
          };
        case 'generalSeriesModule':
          return {
            modality: frame.modality || 'US',
          };
        default:
          return undefined;
      }
    }, 10000);

    ProtectMedSamSegmentationComponentComponent.protectImageLoaderRegistered = true;
  }

  private createToolGroup() {
    const tools = this.cornerstone?.tools;

    if (!tools) {
      return;
    }

    try {
      tools.ToolGroupManager.destroyToolGroup(this.toolGroupId);
    } catch (error) {}

    this.toolGroup = tools.ToolGroupManager.createToolGroup(this.toolGroupId);

    [
      tools.WindowLevelTool,
      tools.PanTool,
      tools.ZoomTool,
      tools.PlanarRotateTool,
      tools.StackScrollTool,
      tools.RectangleROITool,
    ].forEach((tool) => {
      try {
        this.toolGroup?.addTool(tool.toolName);
      } catch (error) {}
    });

    this.toolGroup?.addViewport(this.viewportId, this.renderingEngineId);
    this.applyToolBindings();
  }

  private applyToolBindings() {
    const tools = this.cornerstone?.tools;

    if (!this.toolGroup || !tools) {
      return;
    }

    const mouse = tools.Enums.MouseBindings;

    [
      tools.WindowLevelTool,
      tools.PanTool,
      tools.ZoomTool,
      tools.PlanarRotateTool,
      tools.RectangleROITool,
    ].forEach((tool) => {
      try {
        this.toolGroup.setToolPassive(tool.toolName);
      } catch (error) {}
    });

    this.toolGroup.setToolActive(tools.StackScrollTool.toolName, {
      bindings: [{ mouseButton: mouse.Wheel }],
    });

    this.toolGroup.setToolActive(tools.PanTool.toolName, {
      bindings: [{ mouseButton: mouse.Auxiliary }],
    });

    this.toolGroup.setToolActive(tools.ZoomTool.toolName, {
      bindings: [{ mouseButton: mouse.Secondary }],
    });

    const primaryTool =
      this.activeTool === 'pan'
        ? tools.PanTool
        : this.activeTool === 'zoom'
          ? tools.ZoomTool
          : this.activeTool === 'rotate'
            ? tools.PlanarRotateTool
            : this.activeTool === 'box'
              ? tools.RectangleROITool
              : tools.WindowLevelTool;

    this.toolGroup.setToolActive(primaryTool.toolName, {
      bindings: [{ mouseButton: mouse.Primary }],
    });
  }

  private wireViewerEvents() {
    const element = this.dicomViewport?.nativeElement;
    const core = this.cornerstone?.core;
    const tools = this.cornerstone?.tools;

    if (!element || !core || !tools) {
      return;
    }

    this.stackNewImageListener = (() => {
      this.zone.run(() => {
        const viewport = this.getViewport();
        this.activeFrameIndex = viewport?.getCurrentImageIdIndex() || 0;
        this.syncBoxesFromAnnotations();
      });
    }) as EventListener;

    this.annotationListener = ((event: Event) => {
      const detail = (event as CustomEvent).detail;
      const changedAnnotation = detail?.annotation;

      if (
        changedAnnotation?.metadata?.toolName !==
        tools.RectangleROITool.toolName
      ) {
        return;
      }

      this.zone.run(() => {
        this.keepOnlyLatestBoxForFrame(changedAnnotation);
        this.syncBoxesFromAnnotations();
      });
    }) as EventListener;

    element.addEventListener(
      core.Enums.Events.STACK_NEW_IMAGE,
      this.stackNewImageListener,
    );
    core.eventTarget.addEventListener(
      tools.Enums.Events.ANNOTATION_COMPLETED,
      this.annotationListener,
    );
    core.eventTarget.addEventListener(
      tools.Enums.Events.ANNOTATION_MODIFIED,
      this.annotationListener,
    );
    core.eventTarget.addEventListener(
      tools.Enums.Events.ANNOTATION_REMOVED,
      this.annotationListener,
    );
  }

  private unwireViewerEvents() {
    const element = this.dicomViewport?.nativeElement;
    const core = this.cornerstone?.core;
    const tools = this.cornerstone?.tools;

    if (element && core && this.stackNewImageListener) {
      element.removeEventListener(
        core.Enums.Events.STACK_NEW_IMAGE,
        this.stackNewImageListener,
      );
    }

    if (core && tools && this.annotationListener) {
      core.eventTarget.removeEventListener(
        tools.Enums.Events.ANNOTATION_COMPLETED,
        this.annotationListener,
      );
      core.eventTarget.removeEventListener(
        tools.Enums.Events.ANNOTATION_MODIFIED,
        this.annotationListener,
      );
      core.eventTarget.removeEventListener(
        tools.Enums.Events.ANNOTATION_REMOVED,
        this.annotationListener,
      );
    }

    this.stackNewImageListener = undefined;
    this.annotationListener = undefined;
  }

  private async loadDicomFrames(paths: NonNullable<IImagingDetailed['paths']>) {
    const frames: IProtectDicomFrame[] = [];
    this.loadDiagnostics = [];

    for (const [pathIndex, imagePath] of paths.entries()) {
      const fileName =
        imagePath.path.split(/[/\\]/).pop() || `Image ${pathIndex + 1}`;
      this.loadingMessage = `Reading DICOM file ${pathIndex + 1} / ${paths.length}`;

      try {
        const blob = await firstValueFrom(
          this.imagingService.preview(imagePath.id.toString()),
        );

        if (!(await isDicomBlob(blob, fileName))) {
          this.addLoadDiagnostic(
            fileName,
            `Skipped: payload is not recognized as DICOM (mime=${blob.type || 'n/a'}, size=${blob.size} bytes).`,
          );
          continue;
        }

        const metadata = await this.readDicomMetadata(blob, pathIndex);

        if (!this.isSegmentableDicom(metadata)) {
          this.addLoadDiagnostic(
            fileName,
            `Skipped: ${this.getSegmentabilityIssue(metadata)}`,
          );
          continue;
        }

        const dicom = parseDicom(await blob.arrayBuffer());

        for (let frameIndex = 0; frameIndex < metadata.frames; frameIndex++) {
          const imageId = `protectdicom:${crypto.randomUUID()}`;
          const localFrame = this.createLocalFrame(
            imageId,
            dicom,
            metadata,
            frameIndex,
          );

          ProtectMedSamSegmentationComponentComponent.protectImageFrames.set(
            imageId,
            localFrame,
          );

          frames.push({
            imageId,
            pathId: imagePath.id,
            path: imagePath.path,
            fileName:
              metadata.frames > 1
                ? `${fileName} - Frame ${frameIndex + 1}`
                : fileName,
            frameIndex,
            rows: metadata.rows,
            columns: metadata.columns,
            instanceNumber: metadata.instanceNumber,
            isLoading: false,
            segmentError: false,
          });
        }
      } catch (error) {
        this.addLoadDiagnostic(
          fileName,
          `Skipped: ${(error as Error)?.message || 'unknown parser error'}`,
        );
        console.warn(`Skipping unsupported DICOM object: ${fileName}`, error);
      }
    }

    return frames.sort((a, b) => {
      const instanceDiff = a.instanceNumber - b.instanceNumber;

      if (instanceDiff !== 0) {
        return instanceDiff;
      }

      return a.fileName.localeCompare(b.fileName, undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    });
  }

  private async readDicomMetadata(
    blob: Blob,
    fallbackIndex: number,
  ): Promise<IProtectDicomMetadata> {
    const arrayBuffer = await blob.arrayBuffer();
    const parseDicomFn = this.getDicomParser();
    const dataSet = parseDicomFn(
      new Uint8Array(arrayBuffer),
      {
        untilTag: 'x7fe00010',
      },
    );
    const frames = Number.parseInt(dataSet.string('x00280008') || '1', 10);
    const instanceNumber = Number.parseInt(
      dataSet.string('x00200013') || `${fallbackIndex + 1}`,
      10,
    );

    return {
      frames: Number.isFinite(frames) && frames > 0 ? frames : 1,
      rows: dataSet.uint16('x00280010') || 0,
      columns: dataSet.uint16('x00280011') || 0,
      instanceNumber: Number.isFinite(instanceNumber)
        ? instanceNumber
        : fallbackIndex + 1,
      hasPixelData: !!dataSet.elements['x7fe00010'],
      modality: dataSet.string('x00080060')?.toUpperCase(),
      sopClassUID: dataSet.string('x00080016'),
    };
  }

  private isSegmentableDicom(metadata: IProtectDicomMetadata): boolean {
    const unsupportedModalities = new Set([
      'RTDOSE',
      'RTPLAN',
      'RTSTRUCT',
      'SEG',
      'SR',
      'PR',
      'REG',
    ]);
    const unsupportedSopClassUIDs = new Set([
      '1.2.840.10008.5.1.4.1.1.481.2',
      '1.2.840.10008.5.1.4.1.1.481.3',
      '1.2.840.10008.5.1.4.1.1.481.5',
    ]);

    return (
      metadata.hasPixelData &&
      metadata.rows > 0 &&
      metadata.columns > 0 &&
      !unsupportedModalities.has(metadata.modality || '') &&
      !unsupportedSopClassUIDs.has(metadata.sopClassUID || '')
    );
  }

  private getSegmentabilityIssue(metadata: IProtectDicomMetadata): string {
    const unsupportedModalities = new Set([
      'RTDOSE',
      'RTPLAN',
      'RTSTRUCT',
      'SEG',
      'SR',
      'PR',
      'REG',
    ]);
    const unsupportedSopClassUIDs = new Set([
      '1.2.840.10008.5.1.4.1.1.481.2',
      '1.2.840.10008.5.1.4.1.1.481.3',
      '1.2.840.10008.5.1.4.1.1.481.5',
    ]);

    if (!metadata.hasPixelData) {
      return 'no PixelData (x7fe00010) was found';
    }

    if (metadata.rows <= 0 || metadata.columns <= 0) {
      return `invalid dimensions rows=${metadata.rows}, columns=${metadata.columns}`;
    }

    if (unsupportedModalities.has(metadata.modality || '')) {
      return `unsupported modality ${metadata.modality}`;
    }

    if (unsupportedSopClassUIDs.has(metadata.sopClassUID || '')) {
      return `unsupported SOP Class UID ${metadata.sopClassUID}`;
    }

    return 'did not pass segmentable DICOM checks';
  }

  private addLoadDiagnostic(fileName: string, message: string) {
    this.loadDiagnostics.push(`${fileName}: ${message}`);
  }

  private getDicomParser(): (
    byteArray: Uint8Array,
    options?: { untilTag?: string },
  ) => any {
    const parserModule: any = this.cornerstone?.dicomParser;
    const parseDicomFromRoot = parserModule?.parseDicom;
    if (typeof parseDicomFromRoot === 'function') {
      return parseDicomFromRoot.bind(parserModule);
    }

    const parseDicomFromDefault = parserModule?.default?.parseDicom;
    if (typeof parseDicomFromDefault === 'function') {
      return parseDicomFromDefault.bind(parserModule.default);
    }

    throw new Error(
      'dicom-parser parseDicom function is unavailable in the loaded module.',
    );
  }

  private createLocalFrame(
    imageId: string,
    dicom: IDicomImage,
    metadata: IProtectDicomMetadata,
    frameIndex: number,
  ): IProtectLocalFrame {
    const pixelData = this.extractFramePixelData(dicom, frameIndex);
    const { minPixelValue, maxPixelValue } = this.getPixelRange(pixelData);
    const measuredWidth = maxPixelValue - minPixelValue || 1;
    const windowCenter =
      dicom.windowCenter ?? minPixelValue + measuredWidth / 2;
    const windowWidth = Math.max(1, dicom.windowWidth ?? measuredWidth);

    return {
      imageId,
      pixelData,
      rows: dicom.rows,
      columns: dicom.columns,
      samplesPerPixel: dicom.samplesPerPixel,
      bitsAllocated: dicom.bitsAllocated,
      bitsStored: dicom.bitsStored,
      pixelRepresentation: dicom.pixelRepresentation,
      photometricInterpretation: dicom.photometricInterpretation,
      windowCenter,
      windowWidth,
      rescaleIntercept: dicom.rescaleIntercept,
      rescaleSlope: dicom.rescaleSlope,
      minPixelValue,
      maxPixelValue,
      modality: metadata.modality,
    };
  }

  private extractFramePixelData(
    dicom: IDicomImage,
    frameIndex: number,
  ): Uint8Array | Uint16Array | Int16Array {
    const pixelsPerFrame = dicom.rows * dicom.columns;
    const samplesPerFrame = pixelsPerFrame * dicom.samplesPerPixel;
    const bytesPerSample = dicom.bitsAllocated / 8;
    const byteOffset =
      dicom.pixelDataOffset + frameIndex * samplesPerFrame * bytesPerSample;
    const byteLength = samplesPerFrame * bytesPerSample;

    if (dicom.bitsAllocated === 8) {
      return new Uint8Array(
        dicom.arrayBuffer.slice(byteOffset, byteOffset + byteLength),
      );
    }

    if (dicom.bitsAllocated !== 16) {
      throw new Error(
        `Unsupported DICOM bit depth for Protect viewer: ${dicom.bitsAllocated}`,
      );
    }

    const view = new DataView(dicom.arrayBuffer, byteOffset, byteLength);
    const pixelData =
      dicom.pixelRepresentation === 1
        ? new Int16Array(samplesPerFrame)
        : new Uint16Array(samplesPerFrame);

    for (let index = 0; index < samplesPerFrame; index++) {
      const offset = index * 2;
      pixelData[index] =
        dicom.pixelRepresentation === 1
          ? view.getInt16(offset, true)
          : view.getUint16(offset, true);
    }

    return pixelData;
  }

  private getPixelRange(pixelData: Uint8Array | Uint16Array | Int16Array): {
    minPixelValue: number;
    maxPixelValue: number;
  } {
    let minPixelValue = Number.POSITIVE_INFINITY;
    let maxPixelValue = Number.NEGATIVE_INFINITY;

    for (let index = 0; index < pixelData.length; index++) {
      const value = pixelData[index];
      minPixelValue = Math.min(minPixelValue, value);
      maxPixelValue = Math.max(maxPixelValue, value);
    }

    if (!Number.isFinite(minPixelValue) || !Number.isFinite(maxPixelValue)) {
      return {
        minPixelValue: 0,
        maxPixelValue: 255,
      };
    }

    return {
      minPixelValue,
      maxPixelValue,
    };
  }

  private getViewport(): any | undefined {
    return this.renderingEngine?.getViewport(this.viewportId);
  }

  private async setFrameIndex(index: number, updateResults = true) {
    const viewport = this.getViewport();

    if (!viewport || !this.dicomFrames.length) {
      return;
    }

    const nextIndex = Math.max(0, Math.min(this.dicomFrames.length - 1, index));
    await viewport.setImageIdIndex(nextIndex);
    viewport.render();

    if (updateResults) {
      this.activeFrameIndex = nextIndex;
      this.syncBoxesFromAnnotations();
    }
  }

  private syncBoxesFromAnnotations() {
    this.dicomFrames.forEach((frame) => {
      const frameAnnotation = this.getFrameAnnotation(frame);
      frame.annotationUID = frameAnnotation?.annotationUID;
      frame.box = frameAnnotation
        ? this.getImageBoxForAnnotation(frameAnnotation, frame)
        : undefined;
    });
  }

  private keepOnlyLatestBoxForFrame(changedAnnotation: any) {
    const imageId = changedAnnotation?.metadata?.referencedImageId;

    if (!imageId) {
      return;
    }

    this.getAnnotationsForImageId(imageId)
      .filter((item) => item.annotationUID !== changedAnnotation.annotationUID)
      .forEach((item) => {
        if (item.annotationUID) {
          this.cornerstone?.tools.annotation.state.removeAnnotation(
            item.annotationUID,
          );
        }
      });
  }

  private getFrameAnnotation(frame: IProtectDicomFrame): any | undefined {
    return this.getAnnotationsForImageId(frame.imageId).at(-1);
  }

  private getAnnotationsForImageId(imageId: string): any[] {
    const element = this.dicomViewport?.nativeElement;

    if (!element) {
      return [];
    }

    const tools = this.cornerstone?.tools;

    if (!tools) {
      return [];
    }

    return (
      tools.annotation.state.getAnnotations(
        tools.RectangleROITool.toolName,
        element,
      ) || []
    ).filter((item: any) => item.metadata?.referencedImageId === imageId);
  }

  private getImageBoxForAnnotation(
    item: any,
    frame: IProtectDicomFrame,
  ): IProtectBoundingBox | undefined {
    const vtkImageData = this.getViewport()?.getImageData()?.imageData;
    const points = item?.data?.handles?.points || [];

    const worldToIndex = vtkImageData?.worldToIndex;

    if (!worldToIndex || points.length < 2) {
      return undefined;
    }

    const indexPoints = points.map((point: number[]) =>
      worldToIndex.call(vtkImageData, point as any),
    );
    const xs = indexPoints.map((point: number[]) => point[0]);
    const ys = indexPoints.map((point: number[]) => point[1]);
    const maxX = frame.columns || Math.max(...xs);
    const maxY = frame.rows || Math.max(...ys);

    return {
      x1: this.clamp(Math.round(Math.min(...xs)), 0, maxX),
      y1: this.clamp(Math.round(Math.min(...ys)), 0, maxY),
      x2: this.clamp(Math.round(Math.max(...xs)), 0, maxX),
      y2: this.clamp(Math.round(Math.max(...ys)), 0, maxY),
    };
  }

  private getCanvasBoxForFrame(
    frame: IProtectDicomFrame,
  ): IProtectBoundingBox | undefined {
    const frameAnnotation = this.getFrameAnnotation(frame);
    const viewport = this.getViewport();
    const element = this.dicomViewport?.nativeElement;
    const canvas = element?.querySelector('canvas');
    const points = frameAnnotation?.data?.handles?.points || [];

    if (!viewport || !element || !canvas || points.length < 2) {
      return undefined;
    }

    const canvasPoints = points.map((point: number[]) =>
      viewport.worldToCanvas(point as any),
    );
    const rect = element.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const xs = canvasPoints.map((point: number[]) => point[0] * scaleX);
    const ys = canvasPoints.map((point: number[]) => point[1] * scaleY);

    return {
      x1: this.clamp(Math.round(Math.min(...xs)), 0, canvas.width),
      y1: this.clamp(Math.round(Math.min(...ys)), 0, canvas.height),
      x2: this.clamp(Math.round(Math.max(...xs)), 0, canvas.width),
      y2: this.clamp(Math.round(Math.max(...ys)), 0, canvas.height),
    };
  }

  private exportNativeFramePng(frame: IProtectDicomFrame): Promise<Blob> {
    const localFrame =
      ProtectMedSamSegmentationComponentComponent.protectImageFrames.get(
        frame.imageId,
      );

    if (!localFrame) {
      return Promise.reject(new Error('Protect DICOM frame data is missing.'));
    }

    const viewportProperties = this.getViewport()?.getProperties?.();
    const invert =
      viewportProperties?.invert ??
      localFrame.photometricInterpretation.toUpperCase() === 'MONOCHROME1';
    const voiLower = viewportProperties?.voiRange?.lower;
    const voiUpper = viewportProperties?.voiRange?.upper;
    const windowCenter =
      typeof voiLower === 'number' && typeof voiUpper === 'number'
        ? (voiLower + voiUpper) / 2
        : localFrame.windowCenter;
    const windowWidth =
      typeof voiLower === 'number' && typeof voiUpper === 'number'
        ? Math.max(1, voiUpper - voiLower)
        : localFrame.windowWidth;

    const canvas = document.createElement('canvas');
    canvas.width = localFrame.columns;
    canvas.height = localFrame.rows;
    const context = canvas.getContext('2d');

    if (!context) {
      return Promise.reject(new Error('Canvas rendering is not available.'));
    }

    const imageData = context.createImageData(
      localFrame.columns,
      localFrame.rows,
    );

    if (localFrame.samplesPerPixel === 1) {
      this.fillMonochromeImageData(
        imageData.data,
        localFrame.pixelData,
        windowCenter,
        windowWidth,
        invert,
      );
    } else if (localFrame.samplesPerPixel === 3) {
      this.fillRgbImageData(imageData.data, localFrame.pixelData);
    } else {
      return Promise.reject(
        new Error(
          `Unsupported samples per pixel for Protect export: ${localFrame.samplesPerPixel}`,
        ),
      );
    }

    context.putImageData(imageData, 0, 0);

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Unable to export DICOM frame.'));
        }
      }, 'image/png');
    });
  }

  private fillMonochromeImageData(
    target: Uint8ClampedArray,
    source: Uint8Array | Uint16Array | Int16Array,
    windowCenter: number,
    windowWidth: number,
    invert: boolean,
  ) {
    const lower = windowCenter - windowWidth / 2;

    for (
      let sourceIndex = 0, targetIndex = 0;
      sourceIndex < source.length;
      sourceIndex++, targetIndex += 4
    ) {
      let grayscale = Math.round(
        ((source[sourceIndex] - lower) / windowWidth) * 255,
      );

      grayscale = this.clamp(grayscale, 0, 255);
      grayscale = invert ? 255 - grayscale : grayscale;

      target[targetIndex] = grayscale;
      target[targetIndex + 1] = grayscale;
      target[targetIndex + 2] = grayscale;
      target[targetIndex + 3] = 255;
    }
  }

  private fillRgbImageData(
    target: Uint8ClampedArray,
    source: Uint8Array | Uint16Array | Int16Array,
  ) {
    for (
      let sourceIndex = 0, targetIndex = 0;
      sourceIndex < source.length;
      sourceIndex += 3, targetIndex += 4
    ) {
      target[targetIndex] = source[sourceIndex] as number;
      target[targetIndex + 1] = source[sourceIndex + 1] as number;
      target[targetIndex + 2] = source[sourceIndex + 2] as number;
      target[targetIndex + 3] = 255;
    }
  }

  private captureViewportPng(): Promise<Blob> {
    const sourceCanvas =
      this.dicomViewport?.nativeElement.querySelector('canvas');

    if (!sourceCanvas) {
      return Promise.reject(new Error('No DICOM canvas found.'));
    }

    const canvas = document.createElement('canvas');
    canvas.width = sourceCanvas.width;
    canvas.height = sourceCanvas.height;
    canvas.getContext('2d')?.drawImage(sourceCanvas, 0, 0);

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Unable to export DICOM frame.'));
        }
      }, 'image/png');
    });
  }

  private waitForRender(): Promise<void> {
    const element = this.dicomViewport?.nativeElement;

    if (!element) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const done = () => {
        element.removeEventListener(
          this.cornerstone!.core.Enums.Events.IMAGE_RENDERED,
          done,
        );
        resolve();
      };

      element.addEventListener(
        this.cornerstone!.core.Enums.Events.IMAGE_RENDERED,
        done,
      );
      this.getViewport()?.render();
      setTimeout(done, 250);
    });
  }

  private waitForAngularRender(): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve));
  }

  private withTimeout<T>(
    promise: Promise<T> | undefined,
    timeoutMs: number,
    errorMessage: string,
  ): Promise<T | undefined> {
    if (!promise) {
      return Promise.resolve(undefined);
    }

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(
        () => reject(new Error(errorMessage)),
        timeoutMs,
      );

      promise
        .then((value) => {
          clearTimeout(timeout);
          resolve(value);
        })
        .catch((error) => {
          clearTimeout(timeout);
          reject(error);
        });
    });
  }

  private destroyViewer(clearState = true) {
    this.unwireViewerEvents();

    try {
      this.cornerstone?.tools.ToolGroupManager.destroyToolGroup(
        this.toolGroupId,
      );
    } catch (error) {}

    try {
      this.renderingEngine?.disableElement(this.viewportId);
      this.renderingEngine?.destroy();
    } catch (error) {}

    if (clearState) {
      try {
        this.cornerstone?.tools.annotation.state.removeAllAnnotations();
        ProtectMedSamSegmentationComponentComponent.protectImageFrames.clear();
        this.cornerstone?.core.cache.purgeCache();
      } catch (error) {}
    }

    this.renderingEngine = undefined;
    this.toolGroup = undefined;
  }

  private resetState() {
    this.destroyViewer();
    this.dicomFrames = [];
    this.activeFrameIndex = 0;
    this.activeTool = 'window';
    this.isExamLoading = false;
    this.isSubmitting = false;
    this.isReconstructing = false;
    this.loadingMessage = 'Loading DICOM stack';
    this.loadError = undefined;
    this.loadDiagnostics = [];
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
  }
}
